import { SwiperSlide } from 'swiper/react';
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

import {
    Carousel,
    CarouselWrapper
} from '$partials/base/carousel';

import { IconPlay } from '$partials/icons';
import { MediaController } from 'media-chrome/react';
import { SimpleImage } from '$partials/media';
import { useTrackVisibility } from 'react-intersection-observer-hook';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const getTotalTime = (timeStamp) => {
    const time = timeStamp.split(':')?.reverse();
    const hours = time[2] ? parseInt(time[2]) * 60 * 60 : 0;
    const mins = time[1] ? parseInt(time[1]) * 60 : 0;
    const seconds = parseInt(time[0] || 0);

    return hours + mins + seconds;
};

const Chapter = ({
    chapter,
    current,
    end,
    isPlaying,
    key,
    setTime,
    start,
    startPlay
}) => {
    return (
        <button
            className='w-full bg-surface-raised rounded-lg p-16 text-left relative'
            key={key}
            onClick={() => {
                if (!isPlaying) {
                    startPlay();
                }
                setTime(start);
            }}
            type='button'>
            <span className='block type-style-sans-body-med'>
                {chapter.title}
            </span>

            {chapter.text && (
                <span className='block type-style-sans-body'>
                    {chapter.text}
                </span>
            )}

            <span className='block type-style-sans-body'>
                {chapter.timeStamp}
            </span>

            {isPlaying && current >= start && current < end && current >= 0 ? (
                <span className='block w-full bg-border-default rounded h-2 mt-16'>
                    <span
                        className='block bg-border-active rounded h-full'
                        style={{ width: `${((current - start) / end) * 100}%` }}/>
                </span>
            ) : null}
        </button>
    );
};


const CarouselItems = ({
    chapters,
    current,
    isPlaying,
    playerRef,
    setTime,
    startPlay
}) => {
    return (
        <CarouselWrapper>
            {chapters?.map((chapter, idx) => {
                if (!chapter.id) { return null; }

                const timeStart = getTotalTime(chapter?.timeStamp);
                const timeEnd = chapters[idx+1] ? getTotalTime(chapters[idx+1]?.timeStamp) : playerRef?.current?.duration;
                return (
                    <SwiperSlide
                        aria-roledescription='slide'
                        className='select-none cursor-pointer flex flex-wrap'
                        key={chapter.id}
                        role='group'>
                        <Chapter
                            chapter={chapter}
                            current={current}
                            end={timeEnd}
                            isPlaying={isPlaying}
                            key={chapter.id}
                            setTime={setTime}
                            start={timeStart}
                            startPlay={startPlay} />
                    </SwiperSlide>
                );
            }).filter(Boolean)}
        </CarouselWrapper>
    );
};


/**
 * @type {import('$types/components/partials/modules.d.ts').PanelVideoChaptersBlock}
 */
const PanelVideoChapters = ({
    backgroundColor,
    chapters,
    title,
    video
}) => {
    const videoEmbed = video.videoEmbed;

    const playerRef = useRef(null);

    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [played, setPlayed] = useState(0);
    const [showPoster, setShowPoster] = useState(true);

    const [ref, { isVisible }] = useTrackVisibility({
        rootMargin: '10px'
    });

    const startPlay = () => {
        setIsPlaying(true);
        setIsStarted(true);
    };

    useEffect(() => {
        if (isPlaying) {
            setShowPoster(false);
        }
    }, [isPlaying]);

    const [time, setTime] = useState(isPlaying || videoEmbed?.poster ? 0 : -1);

    const bgColor = `bg-surface-panel-${backgroundColor}`;
    const poster = videoEmbed?.poster || videoEmbed?.embed?.thumbnailUrl;

    const handleTimeUpdate = () => {
        const player = playerRef.current;
        setPlayed(player.currentTime / player.duration);
    };

    const handleDurationChange = () => {
        const player = playerRef.current;
        setDuration(player.duration);
    };

    const handleVolumeChange = (e) => {
        setIsMuted(e.target.muted);
    };

    useEffect(() => {
        if (isVisible && !isStarted) {
            startPlay();
        }
    }, [isVisible]);

    useEffect(() => {
        if (playerRef.current) {
            const player = playerRef.current;
            playerRef.current.currentTime = time;
            setPlayed(time / player.duration);
        }
    }, [time]);

    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.addEventListener('volumechange', handleVolumeChange);
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.removeEventListener('volumechange', handleVolumeChange);
            }
        };
    }, [playerRef?.current]);

    return (
        <section className={`col-wrapper-breakout ${bgColor} rounded-lg pt-24 pb-16`}>
            <div className='col-padding'>
                <h2 className='type-style-sans-h1 w-full md:w-7/12'>
                    {title}
                </h2>

                <div className='mt-40 flex-col-reverse md:flex-row flex gap-16'>
                    {/* Mobile: carousel */}
                    <div className='-mx-8 sm:hidden'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1.2,
                                spaceBetween: 8,
                                grabCursor: false,
                                slidesOffsetBefore: 8,
                                slidesOffsetAfter: 8
                            }}>
                            <CarouselItems
                                chapters={chapters}
                                current={duration * played}
                                isPlaying={isPlaying}
                                playerRef={playerRef}
                                setTime={setTime}
                                startPlay={startPlay}/>
                        </Carousel>
                    </div>

                    {/* Tablet: carousel */}
                    <div className='hidden sm:block md:hidden -mx-8'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 4,
                                spaceBetween: 8,
                                grabCursor: false,
                                slidesOffsetBefore: 8,
                                slidesOffsetAfter: 8
                            }}>
                            <CarouselItems
                                chapters={chapters}
                                current={duration * played}
                                isPlaying={isPlaying}
                                playerRef={playerRef}
                                setTime={setTime}
                                startPlay={startPlay}/>
                        </Carousel>
                    </div>

                    {/* Desktop */}
                    <div className='hidden md:flex flex-col flex-grow gap-y-8 gap-x-8'>
                        {chapters?.map((chapter, idx) => {
                            const timeStart = getTotalTime(chapter?.timeStamp);
                            const timeEnd = chapters[idx+1] ? getTotalTime(chapters[idx+1]?.timeStamp) : playerRef?.current?.duration;
                            return (
                                <Chapter
                                    chapter={chapter}
                                    current={duration * played}
                                    end={timeEnd}
                                    isPlaying={isPlaying}
                                    key={chapter.id}
                                    setTime={setTime}
                                    start={timeStart}
                                    startPlay={startPlay} />
                            );
                        })}
                    </div>

                    <div
                        className='w-full md:w-[1036px] relative'
                        ref={ref}>
                        <MediaController
                            style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                width: '100%',
                                aspectRatio: videoEmbed.width && videoEmbed.height ? `${videoEmbed.width}/${videoEmbed.height}` : '16/9',
                            }}>
                            <ReactPlayer
                                crossOrigin
                                controls={true}
                                light={
                                    !showPoster ? false : (
                                        <SimpleImage
                                            alt='Video poster image'
                                            src={poster} />
                                    )
                                }
                                muted={isMuted}
                                onDurationChange={handleDurationChange}
                                onEnded={() => setShowPoster(true)}
                                onPause={() => setIsPlaying(false)}
                                onPlay={() => {
                                    setIsPlaying(true);
                                    setShowPoster(false);
                                }}
                                onTimeUpdate={handleTimeUpdate}
                                pip={false}
                                playing={isPlaying}
                                playsInline={true}
                                ref={playerRef}
                                slot='media'
                                src={videoEmbed.src}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                title={title}>
                            </ReactPlayer>

                            {showPoster && (
                                <div className='absolute z-50 right-8 bottom-8'>
                                    <button
                                        className='p-12 inline-flex items-center bg-button-tint-surface rounded-full'
                                        onClick={startPlay.bind(null)}
                                        type='button'>
                                        <IconPlay/>
                                    </button>
                                </div>
                            )}
                        </MediaController>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PanelVideoChapters;
